/**
 * Scope across vendors: the same question asked of every contract on file,
 * answered only with the words on the page, side by side. No verdicts; the
 * compliance officer judges.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CATEGORY_LABEL, StatePill } from './ScopeTab';

const API = process.env.REACT_APP_API_URL;
const ROLE_LABEL = { contract: 'Contract', baa: 'BAA', amendment: 'Amendment', addendum: 'Addendum', exhibit: 'Exhibit' };

export default function ScopeCompareModal({ open, onClose }) {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [state, setState] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const p = new URLSearchParams();
        if (q.trim()) p.set('q', q.trim());
        if (category) p.set('category', category);
        if (state) p.set('state', state);
        const r = await fetch(`${API}/api/fdr/scope/search?${p.toString()}`, { credentials: 'include' });
        const d = await r.json();
        setItems(d.success ? d.items || [] : []);
      } catch { setItems([]); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [open, q, category, state]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const byVendor = useMemo(() => {
    const m = new Map();
    for (const i of items) {
      const k = i.entity_id;
      if (!m.has(k)) m.set(k, { entity_id: k, entity_name: i.entity_name, items: [] });
      m.get(k).items.push(i);
    }
    return [...m.values()].sort((a, b) => a.entity_name.localeCompare(b.entity_name));
  }, [items]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-[min(84rem,97vw)] max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <div className="mr-auto">
            <h3 className="text-lg font-bold text-slate-800">Scope across vendors</h3>
            <p className="text-xs text-slate-500">What each contract says is in or out of scope, side by side. Quotes only; you judge.</p>
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search: utilization management, hearing aids, 92557…"
            className="text-xs border border-slate-300 rounded-lg px-3 py-2 text-slate-700 min-w-[280px]" autoFocus />
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-2 text-slate-700">
            <option value="">Every category</option>
            {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={state} onChange={(e) => setState(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-2 text-slate-700">
            <option value="">In and out of scope</option>
            <option value="in_scope">In scope only</option>
            <option value="out_of_scope">Out of scope only</option>
            <option value="not_addressed">Not addressed only</option>
          </select>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none" aria-label="Close">×</button>
        </div>
        <div className="overflow-y-auto px-5 py-4 flex-1">
          {loading && <p className="text-xs text-slate-400">Searching…</p>}
          {!loading && !byVendor.length && <p className="text-sm text-slate-500">No statements match. Read contracts into the register first (Contracts → open a contract → Documents).</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {byVendor.map((v) => (
              <div key={v.entity_id} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm">
                <div className="px-4 py-2.5 border-b border-slate-100">
                  <div className="text-[13px] font-bold text-slate-800">{v.entity_name}</div>
                  <div className="text-[11px] text-slate-400">{v.items.length} statement{v.items.length === 1 ? '' : 's'}</div>
                </div>
                <ul className="divide-y divide-slate-100">
                  {v.items.map((i) => (
                    <li key={i.id} className="px-4 py-2.5">
                      <button type="button" onClick={() => setOpenId(openId === i.id ? null : i.id)} className="w-full text-left">
                        <div className="flex items-start gap-2">
                          <StatePill state={i.state} />
                          <div className="min-w-0">
                            <div className="text-[12.5px] font-semibold text-slate-800">{i.label}</div>
                            <div className="text-[11px] text-slate-400">{CATEGORY_LABEL[i.category] || i.category}{i.file_name ? ` · ${ROLE_LABEL[i.doc_role] || 'Document'}${i.page ? ` p. ${i.page}` : ''}` : ''}{i.confirmed_at ? ' · confirmed' : i.state !== 'not_addressed' ? ' · unconfirmed' : ''}</div>
                          </div>
                        </div>
                      </button>
                      {openId === i.id && (
                        <div className="mt-2">
                          {i.detail && <div className="text-[12px] text-slate-600 mb-1">{i.detail}</div>}
                          {i.quote && <blockquote className="text-[12px] text-slate-700 bg-amber-50/60 border-l-4 border-amber-300 rounded-r-lg px-3 py-2 whitespace-pre-wrap break-words">“{i.quote}”</blockquote>}
                          {Array.isArray(i.codes) && i.codes.length > 0 && <div className="mt-1 text-[11px] text-slate-600">Codes listed: {i.codes.join(', ')}</div>}
                          {i.codes_by_reference && <div className={`mt-1 text-[11px] ${i.reference_page ? 'text-sky-800' : 'text-amber-800'}`}>Defined by reference to {i.codes_by_reference}{i.reference_page ? ` (in this document, page ${i.reference_page})` : ' (not on file)'}.</div>}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-400">
          <span>{items.length} statement{items.length === 1 ? '' : 's'} across {byVendor.length} vendor{byVendor.length === 1 ? '' : 's'}</span>
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50">Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
